import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { useEvieVoiceStore } from '@/store/evieVoice';
import { fetchPreferences } from '@/lib/api';

/**
 * Hydrates the driver's SERVER-side EVII voice choice (User.evie_voice) into
 * the local evieVoice store once per authenticated load, so the choice
 * follows the driver across devices — a fresh device with an empty
 * localStorage cache picks up the server value instead of falling back to
 * the 'female' default.
 *
 * The server is the source of truth for cross-device; the local cache stays
 * the source for immediate playback (the tour greeting + DriverAssistSheet
 * read it synchronously). setVoiceChoice writes back to the server, so this
 * hook + that setter keep the two in sync. Shares the ['preferences'] query
 * key with the Settings page so there's a single fetch.
 *
 * No-op when unauthenticated.
 */
export function useEvieVoiceSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?._id);
  const hydrateVoice = useEvieVoiceStore((s) => s.hydrateVoice);

  const q = useQuery({
    queryKey: ['preferences'],
    queryFn: fetchPreferences,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    const v = q.data?.evie_voice;
    if (v === 'female' || v === 'male') hydrateVoice(userId, v);
  }, [isAuthenticated, userId, q.data?.evie_voice, hydrateVoice]);
}
